import dayjs from 'dayjs';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Box, Button, Popover, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

const noop = () => {};

function DateRangePicker({
  startDate,
  endDate,
  onDateRangeChange,
  disabled = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRange, setSelectedRange] = useState([
    startDate ? dayjs(startDate) : null,
    endDate ? dayjs(endDate) : null,
  ]);

  // Update internal state if external props change
  useEffect(() => {
    setSelectedRange([
      startDate ? dayjs(startDate) : null,
      endDate ? dayjs(endDate) : null,
    ]);
  }, [startDate, endDate]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStartDateChange = (newValue) => {
    setSelectedRange([newValue, selectedRange[1]]);
  };

  const handleEndDateChange = (newValue) => {
    setSelectedRange([selectedRange[0], newValue]);
  };

  const handleApply = () => {
    if (selectedRange[0] && selectedRange[1]) {
      if (selectedRange[0].isAfter(selectedRange[1])) {
        toast.error('Start date cannot be after end date.');
        return;
      }
      // Format dates as YYYY-MM-DD for the backend
      onDateRangeChange(
        selectedRange[0].format('YYYY-MM-DD'),
        selectedRange[1].format('YYYY-MM-DD'),
      );
      handleClose();
    } else {
      toast.error('Please select both start and end dates.');
    }
  };

  // Handle quick select date ranges
  const handleQuickSelect = (days) => {
    const newEndDate = dayjs().subtract(1, 'day'); // Yesterday
    const newStartDate = dayjs().subtract(days, 'days');
    onDateRangeChange(
      newStartDate.format('YYYY-MM-DD'),
      newEndDate.format('YYYY-MM-DD'),
    );
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  // Display text for the button
  const displayDateRange = (start, end) => {
    if (start && end) {
      return `${dayjs(start).format('MMM D, YYYY')} - ${dayjs(end).format(
        'MMM D, YYYY',
      )}`;
    }
    return 'Select Date Range';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Button
          aria-describedby={id}
          variant='outlined'
          color='inherit'
          onClick={disabled ? noop : handleClick}
          disabled={disabled}
          startIcon={<CalendarTodayIcon />}
        >
          {displayDateRange(startDate, endDate)}
        </Button>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableScrollLock
        >
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'row', gap: 3 }}>
            {/* Quick Selects Column */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pr: 2,
                borderRight: '1px solid #eee',
              }}
            >
              <Typography variant='subtitle1' sx={{ mb: 1 }}>
                Quick Select
              </Typography>
              <Button size='small' onClick={() => handleQuickSelect(7)}>
                Last 7 days
              </Button>
              <Button size='small' onClick={() => handleQuickSelect(28)}>
                Last 28 days
              </Button>
              <Button size='small' onClick={() => handleQuickSelect(90)}>
                Last 90 days
              </Button>
              {/* Add more quick selects as needed */}
            </Box>

            {/* Custom Date Range Column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='subtitle1'>Custom Range</Typography>
              <DatePicker
                label='Start Date'
                value={selectedRange[0]}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField {...params} size='small' />}
                maxDate={dayjs().subtract(1, 'day')}
              />
              <DatePicker
                label='End Date'
                value={selectedRange[1]}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField {...params} size='small' />}
                minDate={selectedRange[0] || null}
                maxDate={dayjs().subtract(1, 'day')}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleClose} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button variant='contained' onClick={handleApply}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}

export default DateRangePicker;
